import React from 'react';

import {
  ControlPointDuplicate,
  DarkMode,
  Description,
  Groups,
  LightMode,
  LockReset,
  Logout,
  NotificationsActive,
  Paid,
  Person,
  PrivacyTip,
  Science,
  SettingsBrightness,
  Smartphone,
  Store,
  SupportAgent,
} from '@mui/icons-material';
import type { DrawerProps } from '@mui/material';
import {
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { AppContext } from '../../contexts/app_context';
import { createScopedI18n } from '../../i18n/i18n';
import AccountEditIcon from '../icons/account_edit';

export type AppDrawerProps = DrawerProps;

const appDrawerI18n = createScopedI18n('components.app_drawer');

export const AppDrawer = ({ onClose, ...props }: AppDrawerProps) => {
  const isMobile = useMediaQuery('(hover: none)');

  const { contextThemeMode, setContextThemeMode } = React.useContext(AppContext);

  const themeModeChangeHandler = React.useCallback(
    (_event: unknown, value: 'light' | 'system' | 'dark') => {
      if (value !== contextThemeMode) {
        setContextThemeMode(value);
      }
    },
    [contextThemeMode, setContextThemeMode],
  );

  const closeDrawerOnItemClickHandler = React.useCallback(() => {
    onClose?.({}, 'backdropClick');
  }, [onClose]);

  return (
    <Drawer
      onClose={onClose}
      {...props}
      PaperProps={{ ...props.PaperProps, sx: { width: '75%', maxWidth: 300, ...props.PaperProps?.sx } }}
    >
      {isMobile ? <div style={{ flex: 1 }} /> : null}
      <Divider />

      <List sx={{ paddingLeft: 1 }}>
        <ListItemButton component={Link} to="/restaurants" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <Store />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('restaurants')} />
        </ListItemButton>

        <ListItemButton component={Link} to="/users" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <Groups />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('users')} />
        </ListItemButton>

        <ListItemButton component={Link} to="/chain" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <Person />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('chain')} />
        </ListItemButton>

        <ListItemButton component={Link} to="/user/edit" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <AccountEditIcon />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('user')} />
        </ListItemButton>

        <ListItemButton component={Link} to="/notifications" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <NotificationsActive />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('notification')} />
        </ListItemButton>

        <ListItemButton component={Link} to="/credit" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <Paid />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('credit')} />
        </ListItemButton>

        <ListItemButton component={Link} to="/credit/purchase" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('credit_purchase')} />
        </ListItemButton>
      </List>

      <Divider />

      <List sx={{ paddingLeft: 1 }}>
        <ListItemButton component={Link} to="/user/edit#change_phone_number" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <Smartphone />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('change_phone_number')} />
        </ListItemButton>

        <ListItemButton component={Link} to="/user/edit#change_password" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <LockReset />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('change_password')} />
        </ListItemButton>

        <ListItemButton
          href="https://www.findtemp.in.th/term-condition"
          target="_blank"
          rel="noopener"
          onClick={closeDrawerOnItemClickHandler}
        >
          <ListItemIcon>
            <Description />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('term_condition')} />
        </ListItemButton>

        <ListItemButton
          href="https://www.findtemp.in.th/privacy-policy"
          target="_blank"
          rel="noopener"
          onClick={closeDrawerOnItemClickHandler}
        >
          <ListItemIcon>
            <PrivacyTip />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('privacy_policy')} />
        </ListItemButton>

        <ListItemButton component={Link} to="/contact_us" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <SupportAgent />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('contact_us')} />
        </ListItemButton>
      </List>

      <Divider />

      <List sx={{ paddingX: 1 }}>
        <ToggleButtonGroup exclusive fullWidth value={contextThemeMode ?? 'system'} onChange={themeModeChangeHandler}>
          <ToggleButton value="light">
            <LightMode />
          </ToggleButton>
          <ToggleButton value="system">
            <SettingsBrightness />
          </ToggleButton>
          <ToggleButton value="dark">
            <DarkMode />
          </ToggleButton>
        </ToggleButtonGroup>
      </List>

      <Divider />

      <List
        sx={{
          paddingLeft: 1,
          marginTop: 'auto',
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'inherit',
          backgroundImage: 'inherit',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
        }}
      >
        {import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging' ? (
          <ListItemButton component={Link} to="/playground" onClick={closeDrawerOnItemClickHandler}>
            <ListItemIcon>
              <Science />
            </ListItemIcon>
            <ListItemText primary="Playground" />
          </ListItemButton>
        ) : null}

        <ListItemButton component={Link} to="/sign_out" onClick={closeDrawerOnItemClickHandler}>
          <ListItemIcon>
            <Logout color="error" />
          </ListItemIcon>
          <ListItemText primary={appDrawerI18n('sign_out')} />
        </ListItemButton>
      </List>

      <Divider />
    </Drawer>
  );
};
